import { memo, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ThumbsUp, ThumbsDown, Brain, Wrench, Speech, Terminal, Navigation, CheckCircle2 } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { ChatMessage as ChatMessageType } from '../../types';
import { navigateWithThreadPreservation, withAgentHighlight } from '../../utils/thread-navigation';
import { getObjectUpdatePills } from '../../utils/object-update-utils';
import {
  ATTACHMENT_CHIP_CLASS,
  getAttachmentIconForMime,
} from '@/components/chat/attachment-chip';
import { ChatSuggestionsInThread } from '@/components/chat/chat-suggestions-in-thread';
import { VoiceStatusLine } from '@/components/chat/voice-status-line';
import { ChatUserAvatar, ChatAgentAvatar } from '@/components/chat/chat-message-avatars';
import {
  CHAT_MESSAGE_PROSE,
  CHAT_USER_BUBBLE,
  CHAT_ASSISTANT_BUBBLE,
  CHAT_DOCUMENT_ROW,
  CHAT_DOCUMENT_SHELL,
  CHAT_DOCUMENT_SUGGESTIONS_SHELL,
  CHAT_DOCUMENT_USER_BUBBLE,
  getChatMessageStyle,
} from '@/components/chat/chat-message-styles';

// Helper function to get the delta icon component
const getDeltaIcon = (deltaType: string) => {
  switch (deltaType) {
    case 'memory':
      return Brain;
    case 'tool':
    case 'content_update':  // Tool-triggered content updates use tool icon
    case 'content_refresh': // Tool-triggered content refreshes use tool icon
      return Wrench;
    case 'voice':
      return Speech;
    case 'system':
      return Terminal;
    case 'navigation':
      return Navigation;
    default:
      // Runtime validation: log error if unknown deltaType
      console.error(`[CHAT MESSAGE] Unknown deltaType: ${deltaType}, defaulting to Terminal icon`);
      return Terminal;
  }
};

const POSITIVE_TAGS = ['Helpful', 'Clear', 'Grounded', 'Actionable', 'Respectful'];
const NEGATIVE_TAGS = ['Incorrect', 'Off-topic', 'Unhelpful', 'Hallucinated', 'Unsafe'];

interface ChatMessageProps {
  message: ChatMessageType;
  /** True while this assistant text block is actively streaming (renders trailing caret). */
  streaming?: boolean;
  /** True while this voice line is the latest for an in-progress turn (drives shimmer). */
  voiceShimmerActive?: boolean;
  onFeedback?: (runId: string, score?: number, tags?: string[], comment?: string) => void;
  onOpenNavigationIntent?: (messageId: string) => void;
  onCancelNavigationIntent?: (messageId: string) => void;
  onSendSuggestion?: (message: string) => void;
  chatSuggestionsMaxPills?: number;
  chatSuggestionsDisabled?: boolean;
  /** Classic mode only — profile photo for user messages. */
  userAvatarMode?: 'icon' | 'profile';
  userAvatarUrl?: string | null;
  /** Classic mode only — identicon seed when no photo. */
  userAvatarColorSeed?: string;
}

/** Memoized: unchanged messages keep object identity across SSE updates, so markdown bodies skip re-render. */
export const ChatMessage = memo(function ChatMessage({
  message,
  streaming = false,
  voiceShimmerActive = false,
  onFeedback,
  onOpenNavigationIntent,
  onCancelNavigationIntent,
  onSendSuggestion,
  chatSuggestionsMaxPills = 4,
  chatSuggestionsDisabled = false,
  userAvatarMode = 'icon',
  userAvatarUrl,
  userAvatarColorSeed,
}: ChatMessageProps) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [showFeedback, setShowFeedback] = useState(false);
  const [selectedScore, setSelectedScore] = useState<number | null>(null);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [now, setNow] = useState(Date.now());
  const navigationIntent = message.messageType === 'navigation_intent' ? message.navigationIntent : undefined;

  useEffect(() => {
    if (!navigationIntent || navigationIntent.status !== 'pending' || !navigationIntent.opensAt) return;
    const interval = window.setInterval(() => setNow(Date.now()), 500);
    return () => window.clearInterval(interval);
  }, [navigationIntent?.opensAt, navigationIntent?.status]);

  // Check if feedback can be shown (only for main assistant messages, not deltas or object updates)
  const canShowFeedback =
    !message.isUser &&
    !message.isDelta &&
    message.runId &&
    onFeedback &&
    message.messageType !== 'object_update' &&
    message.messageType !== 'chat_suggestions';

  const handleVote = (score: number) => {
    console.log('🎯 [FEEDBACK UI] Vote clicked:', { score, runId: message.runId });
    setSelectedScore(score);
    setShowFeedback(true);
  };

  const handleTagToggle = (tag: string) => {
    setSelectedTags(prev => 
      prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]
    );
  };

  const handleSubmit = async () => {
    if (!message.runId || !onFeedback || selectedScore === null) return;
    
    console.log('📤 [FEEDBACK UI] Submitting:', { 
      runId: message.runId, 
      score: selectedScore, 
      tags: selectedTags, 
      comment 
    });
    
    setIsSubmitting(true);
    await onFeedback(message.runId, selectedScore, selectedTags.length > 0 ? selectedTags : undefined, comment || undefined);
    setIsSubmitting(false);
    setShowFeedback(false);
    setSelectedScore(null);
    setSelectedTags([]);
    setComment('');
  };

  const handleSkip = async () => {
    if (!message.runId || !onFeedback || selectedScore === null) return;
    
    console.log('⏭️ [FEEDBACK UI] Skipping details, just submitting score');
    setIsSubmitting(true);
    await onFeedback(message.runId, selectedScore);
    setIsSubmitting(false);
    setShowFeedback(false);
    setSelectedScore(null);
    setSelectedTags([]);
    setComment('');
  };

  const handleCancel = () => {
    setShowFeedback(false);
    setSelectedScore(null);
    setSelectedTags([]);
    setComment('');
  };

  const availableTags = selectedScore === 1 ? POSITIVE_TAGS : NEGATIVE_TAGS;
  const objectUpdatePills =
    message.messageType === 'object_update' && message.objectUpdate
      ? getObjectUpdatePills(message.objectUpdate)
      : [];
  const isObjectUpdateMessage = objectUpdatePills.length > 0;
  const isNavigationIntentMessage = !!navigationIntent;
  const isChatSuggestionsMessage = message.messageType === 'chat_suggestions';
  const isDocumentStyle = getChatMessageStyle() === 'document';
  const showAvatarColumn =
    !isDocumentStyle &&
    !message.isDelta &&
    message.messageType !== 'object_update' &&
    message.messageType !== 'navigation_intent' &&
    !isChatSuggestionsMessage;
  const feedbackControls = (inlineDocument = false) =>
    canShowFeedback ? (
      <div className={`flex gap-2 ${inlineDocument ? 'mt-1.5' : 'mt-2'}`}>
        <button
          onClick={() => handleVote(1)}
          className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-green-600 transition-colors"
          title="Helpful"
        >
          <ThumbsUp
            className="h-4 w-4"
            fill={message.feedback?.score === 1 ? 'currentColor' : 'none'}
          />
        </button>
        <button
          onClick={() => handleVote(0)}
          className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-red-600 transition-colors"
          title="Not helpful"
        >
          <ThumbsDown
            className="h-4 w-4"
            fill={message.feedback?.score === 0 ? 'currentColor' : 'none'}
          />
        </button>
      </div>
    ) : null;

  const feedbackPopover = () =>
    showFeedback && canShowFeedback ? (
      <div className="mt-2 p-4 bg-background border rounded-lg shadow-lg">
        <div className="space-y-3">
          <div>
            <label className="text-xs font-medium">Tags (optional)</label>
            <div className="flex flex-wrap gap-2 mt-1">
              {availableTags.map(tag => (
                <button
                  key={tag}
                  onClick={() => handleTagToggle(tag)}
                  className={`px-2 py-1 text-xs rounded ${
                    selectedTags.includes(tag)
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted hover:bg-muted/80'
                  }`}
                >
                  {tag}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-xs font-medium">Comment (optional)</label>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="What could be improved?"
              className="w-full mt-1 p-2 text-xs border rounded bg-background"
              rows={2}
            />
          </div>

          <div className="flex gap-2 justify-end">
            <button
              onClick={handleCancel}
              className="px-3 py-1 text-xs rounded hover:bg-muted"
            >
              Cancel
            </button>
            <button
              onClick={handleSkip}
              disabled={isSubmitting}
              className="px-3 py-1 text-xs rounded hover:bg-muted"
            >
              Skip
            </button>
            <button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="px-3 py-1 text-xs rounded bg-primary text-primary-foreground hover:bg-primary/90"
            >
              {isSubmitting ? 'Submitting...' : 'Submit'}
            </button>
          </div>
        </div>
      </div>
    ) : null;

  const navigationLabel = navigationIntent?.label || 'page';
  const remainingSeconds =
    navigationIntent?.opensAt && navigationIntent.status === 'pending'
      ? Math.max(0, Math.ceil((navigationIntent.opensAt - now) / 1000))
      : null;

  if (message.isDelta && message.deltaType === 'voice') {
    return (
      <VoiceStatusLine
        message={message as ChatMessageType & { isDelta: true; deltaType: 'voice' }}
        streaming={voiceShimmerActive}
      />
    );
  }

  return (
    <div
      className={
        isDocumentStyle
          ? `agent-block-enter ${isChatSuggestionsMessage ? '-mt-2' : ''}`
          : `agent-block-enter flex gap-3 ${message.isUser ? 'flex-row-reverse' : ''}`
      }
    >
      {showAvatarColumn && (
        message.isUser ? (
          <ChatUserAvatar
            mode={userAvatarMode}
            avatarUrl={userAvatarUrl}
            colorSeed={userAvatarColorSeed || 'user'}
          />
        ) : (
          <ChatAgentAvatar />
        )
      )}
      <div className={isDocumentStyle ? undefined : 'flex-1'}>
        {isChatSuggestionsMessage && message.suggestionChips?.length ? (
          isDocumentStyle ? (
            <div className={CHAT_DOCUMENT_ROW}>
              <div className={CHAT_DOCUMENT_SUGGESTIONS_SHELL}>
                <ChatSuggestionsInThread
                  chips={message.suggestionChips}
                  maxPills={chatSuggestionsMaxPills}
                  disabled={chatSuggestionsDisabled}
                  onSendSuggestion={(text) => onSendSuggestion?.(text)}
                />
              </div>
            </div>
          ) : (
            <ChatSuggestionsInThread
              chips={message.suggestionChips}
              maxPills={chatSuggestionsMaxPills}
              disabled={chatSuggestionsDisabled}
              onSendSuggestion={(text) => onSendSuggestion?.(text)}
            />
          )
        ) : message.isDelta ? (
          // Delta message - inline icon with no avatar
          <div className="flex items-start gap-2 text-xs">
            <div className="flex-shrink-0 mt-0.5">
              {(() => {
                // TypeScript ensures deltaType is present when isDelta is true
                const IconComponent = getDeltaIcon(message.deltaType);
                return (
                  <IconComponent
                    className="h-4 w-4 text-muted-foreground"
                  />
                );
              })()}
            </div>
            <div className="flex-1 text-muted-foreground font-mono leading-relaxed">
              {message.content}
            </div>
          </div>
        ) : isNavigationIntentMessage ? (
          <div className="flex flex-col items-end gap-1.5">
            <div className="inline-flex max-w-full flex-col items-end gap-1 rounded-md border border-transparent px-1 py-0.5">
              <div className="inline-flex flex-wrap items-center justify-end gap-1.5">
                <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2.5 py-1 text-xs text-muted-foreground">
                  <Navigation className="h-3.5 w-3.5 shrink-0" />
                  <span className="text-left">
                    {navigationIntent.status === 'opened'
                      ? `Opened ${navigationLabel}`
                      : navigationIntent.status === 'cancelled'
                        ? `Stayed here`
                        : `Opening ${navigationLabel}`}
                  </span>
                </span>
              </div>
              <span className="max-w-[min(100%,24rem)] text-right text-xs text-muted-foreground">
                {navigationIntent.status === 'pending'
                  ? remainingSeconds !== null
                    ? `${navigationIntent.reason || `Showing ${navigationLabel}`} in ${remainingSeconds}s`
                    : navigationIntent.reason || `${navigationLabel} is ready to open`
                  : navigationIntent.status === 'opened'
                    ? navigationIntent.reason || `${navigationLabel} opened`
                    : `Navigation cancelled`}
              </span>
              <div className="inline-flex items-center gap-2 text-xs">
                {navigationIntent.status === 'pending' ? (
                  <>
                    <button
                      type="button"
                      className="text-foreground underline-offset-2 hover:underline"
                      onClick={() => onOpenNavigationIntent?.(message.id)}
                    >
                      Open now
                    </button>
                    <button
                      type="button"
                      className="text-muted-foreground underline-offset-2 hover:underline"
                      onClick={() => onCancelNavigationIntent?.(message.id)}
                    >
                      Stay here
                    </button>
                  </>
                ) : navigationIntent.target_path ? (
                  <button
                    type="button"
                    className="text-muted-foreground underline-offset-2 hover:underline"
                    onClick={() => onOpenNavigationIntent?.(message.id)}
                  >
                    Open again
                  </button>
                ) : null}
              </div>
            </div>
          </div>
        ) : isObjectUpdateMessage ? (
          <div className="flex flex-col items-end gap-1.5">
            {objectUpdatePills.map((pill, idx) => {
              const targetPath = pill.target_path;
              const labelPrimary =
                pill.display_label || `${pill.object_type} ${pill.action}`;
              const fieldsHint =
                pill.changed_fields?.length && pill.changed_fields.join(', ');
              const subline =
                pill.description ??
                (fieldsHint ? `Fields: ${fieldsHint}` : undefined) ??
                pill.object_name ??
                pill.object_id;
              const via = pill.source_tool && !pill.description ? ` · via ${pill.source_tool}` : '';
              const pathToOpen =
                targetPath && pill.focus_key
                  ? withAgentHighlight(targetPath, pill.focus_key)
                  : targetPath;
              const row = (
                <div
                  key={pill.object_update_id || `${pill.object_type}-${pill.object_id}-${idx}`}
                  className="inline-flex max-w-full flex-col items-end gap-0.5 rounded-md border border-transparent px-1 py-0.5"
                >
                  <div className="inline-flex flex-wrap items-center justify-end gap-1.5">
                    <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2.5 py-1 text-xs text-muted-foreground">
                      <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />
                      <span className="text-left">{labelPrimary}</span>
                    </span>
                  </div>
                  {(subline || via) && (
                    <span className="max-w-[min(100%,24rem)] text-right text-xs text-muted-foreground">
                      {subline}
                      {via}
                    </span>
                  )}
                </div>
              );
              if (pathToOpen) {
                return (
                  <button
                    type="button"
                    onClick={() =>
                      navigateWithThreadPreservation(navigate, pathToOpen, searchParams)
                    }
                    className="cursor-pointer text-left transition-opacity hover:opacity-80"
                    role="link"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        navigateWithThreadPreservation(navigate, pathToOpen, searchParams);
                      }
                    }}
                  >
                    {row}
                  </button>
                );
              }
              return row;
            })}
          </div>
        ) : (
          (() => {
            const attRow =
              message.attachments && message.attachments.length > 0 ? (
                <div
                  className={`flex w-full flex-wrap gap-1.5 ${message.isUser ? 'justify-end' : 'justify-start'}`}
                >
                  {message.attachments.map((att) => {
                    const IconComponent = getAttachmentIconForMime(att.mime_type);
                    const label = att.original_name || 'file';
                    const chipInner = (
                      <span className={ATTACHMENT_CHIP_CLASS} title={label}>
                        <IconComponent className="h-3.5 w-3.5 shrink-0" />
                        <span className="truncate">{label}</span>
                        {att.status === 'failed' ? (
                          <span className="text-destructive" title={att.error || 'Failed'}>
                            !
                          </span>
                        ) : null}
                      </span>
                    );
                    if (att.file_url) {
                      return (
                        <a
                          key={att.id}
                          href={att.file_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex"
                        >
                          {chipInner}
                        </a>
                      );
                    }
                    return (
                      <span key={att.id} className="inline-flex">
                        {chipInner}
                      </span>
                    );
                  })}
                </div>
              ) : null;

            const bubbleChrome = message.isUser
              ? isDocumentStyle
                ? CHAT_DOCUMENT_USER_BUBBLE
                : CHAT_USER_BUBBLE
              : isDocumentStyle
                ? ''
                : CHAT_ASSISTANT_BUBBLE;

            const proseBubble = message.content?.trim() ? (
              <div
                className={`${CHAT_MESSAGE_PROSE} ${bubbleChrome} ${streaming ? 'streaming-caret' : ''}`.trim()}
              >
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={{
                    a: ({ ...props }) => (
                      <a target="_blank" rel="noopener noreferrer" {...props} />
                    ),
                    p: ({ ...props }) => (
                      <p className="mb-4 mt-0 text-sm leading-relaxed last:mb-0" {...props} />
                    ),
                    ul: ({ ...props }) => (
                      <ul
                        className="list-disc pl-6 my-2 space-y-1"
                        style={{ listStyleType: 'disc' }}
                        {...props}
                      />
                    ),
                    ol: ({ ...props }) => (
                      <ol
                        className="list-decimal pl-6 my-2 space-y-1"
                        style={{ listStyleType: 'decimal' }}
                        {...props}
                      />
                    ),
                    li: ({ ...props }) => <li className="text-sm pl-1" {...props} />,
                  }}
                >
                  {message.content}
                </ReactMarkdown>
              </div>
            ) : null;

            if (message.isUser) {
              return (
                <div className={isDocumentStyle ? CHAT_DOCUMENT_ROW : 'flex w-full min-w-0 flex-col items-end gap-1.5'}>
                  {attRow}
                  {proseBubble}
                </div>
              );
            }
            if (isDocumentStyle) {
              return (
                <div className={CHAT_DOCUMENT_ROW}>
                  <div className={CHAT_DOCUMENT_SHELL}>
                    {attRow}
                    {proseBubble}
                    {feedbackControls(true)}
                    {feedbackPopover()}
                  </div>
                </div>
              );
            }
            return (
              <div className="flex w-full min-w-0 flex-col items-start gap-1.5">
                {attRow}
                {proseBubble}
              </div>
            );
          })()
        )}
        
        {!isDocumentStyle && feedbackControls()}
        {!isDocumentStyle && feedbackPopover()}
      </div>
    </div>
  );
});
